import { axios } from "@/libraries/axios";
import { cn } from "@/libraries/cn";
import { QUERY_KEYS, SUBSCRIPTION_STATUS } from "@/libraries/constant";
import { Button } from "@mui/material";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useQuery } from "@tanstack/react-query";
import { createElement, useMemo } from "react";
import type { ValueOf } from "type-fest";
import { Skeleton } from "../other";
import { Typography } from "../text";

export interface SubscribtionPlanType {
	value: ValueOf<typeof SUBSCRIPTION_STATUS>;
	title: string;
	description: string;
	items: {
		icon: any;
		text: string;
	}[];
}

interface SubscriptionPlanProps extends React.ComponentPropsWithoutRef<"div"> {
	plan: SubscribtionPlanType;
	currentPlan?: SubscribtionPlanType["value"];
	onClickUpgrade: (val: SubscribtionPlanType["value"]) => void;
}

const SubscriptionPlan: React.FC<SubscriptionPlanProps> = ({
	plan,
	currentPlan,
	className,
	onClickUpgrade,
	...props
}) => {
	const planQuery = useQuery({
		queryKey: [QUERY_KEYS.GET_PLAN, plan.value],
		queryFn: ({ signal }) =>
			axios.get("/api/v1/checkouts", {
				params: {
					tier: plan.value,
				},
				signal,
			}),
		enabled: !plan.value.includes(SUBSCRIPTION_STATUS.Free),
	});

	const startFrom = useMemo(() => {
		if (planQuery.data?.data) {
			const monthly = planQuery.data.data.data.find(
				(d: any) => d.name.toLowerCase() === "monthly",
			);
			if (monthly?.description) return `Start from ${monthly.description}`;
		}
		return null;
	}, [planQuery.data?.data]);

	const showUpgrade = useMemo(
		() =>
			planQuery.isSuccess &&
			currentPlan &&
			plan.value !== currentPlan &&
			!plan.value.includes("free") &&
			currentPlan !== SUBSCRIPTION_STATUS.PRO,
		[planQuery.isSuccess, currentPlan, plan.value],
	);

	return (
		<div
			className={cn(
				"w-full rounded-2xl border bg-white flex flex-col items-start justify-start",
				{
					"border-zinc-300": plan.value === currentPlan,
					"border-zinc-500": plan.value !== currentPlan,
				},
				className,
			)}
			{...props}
		>
			{plan.value === currentPlan ? (
				<div className="bg-primary-light w-full rounded-t-2xl px-4 py-2">
					<Typography fontWeight={500} fontSize={12} className="text-primary">
						Current Plan
					</Typography>
				</div>
			) : null}
			<div className="w-full px-4 py-3 flex flex-col gap-2">
				<div className="w-full">
					<Typography fontWeight={600} fontSize={16}>
						{plan.title}
					</Typography>
					<Typography fontWeight={400} fontSize={14}>
						{plan.description}
					</Typography>
					{planQuery.isLoading ? (
						<Skeleton className="w-1/3 h-6" />
					) : startFrom ? (
						<Typography fontWeight={500} fontSize={14} className="text-primary">
							{startFrom}
						</Typography>
					) : null}
				</div>
				<Divider />
				{plan.items?.length ? (
					<List dense={true} className="!py-0">
						{plan.items.map((item) => (
							<ListItem key={item.text} className="!gap-2 !p-0">
								<ListItemIcon className="!block !min-w-0">
									{createElement(item.icon, {
										fontSize: "small",
										className: "text-zinc-950 !text-base",
									})}
								</ListItemIcon>
								<ListItemText
									primary={item.text}
									className="font-normal text-sm"
								/>
							</ListItem>
						))}
					</List>
				) : null}
				{showUpgrade ? (
					<Button
						size="large"
						color="primary"
						variant="contained"
						className="!rounded-full !h-12"
						onClick={() => onClickUpgrade(plan.value)}
					>
						Upgrade
					</Button>
				) : null}
			</div>
		</div>
	);
};

export default SubscriptionPlan;
